import { Injectable, Type } from "@angular/core";
import { DeepLinkMetadata, getAllDeepLinks } from 'app/app-page-helpers';
import { CsPageComponentInterface } from "providers/nav-controller";
import { parseUrlToPages, pagesToUrl, type PageWithParams } from 'utils/url-parser';

export interface NavSegment extends DeepLinkMetadata {
  id: string;
  data: any;
  breadcrumbs?: string;
  defaultHistory?: Type<CsPageComponentInterface<any>>[];
}

@Injectable()
export class UrlService {

  constructor() { }


  /**
   * Normalize a URL by ensuring it starts with '/' and doesn't end with '/'
   */
  normalizeUrl(browserUrl: string): string {
    browserUrl = browserUrl.trim();
    if (browserUrl.charAt(0) !== '/') {
      // ensure first char is a /
      browserUrl = '/' + browserUrl;
    }
    if (browserUrl.length > 1 && browserUrl.charAt(browserUrl.length - 1) === '/') {
      // ensure last char is not a /
      browserUrl = browserUrl.substring(0, browserUrl.length - 1);
    }
    return browserUrl;
  }

  /**
   * Get the current segment from a browser URL
   */
  getCurrentSegment(browserUrl: string): NavSegment {
    // When URL is just '/', parse will return null and we'll handle it in preShowNavAsync
    return this.parse(browserUrl);
  }

  parse(browserUrl: string): NavSegment {
    // Use the unified parser
    const pages = parseUrlToPages(browserUrl);

    // If no pages found, return null
    if (pages.length === 0) {
      return null;
    }

    // Get the current page (last in array)
    const currentPage = pages[pages.length - 1];

    // Find the matching deep link for the current page
    const deepLink = getAllDeepLinks().find(link => {
      if (!link.segment) return false;
      const segmentBaseName = link.segment.split('/')[0];
      return segmentBaseName === currentPage.segment;
    });

    // If no matching deep link found, return null
    if (!deepLink) {
      return null;
    }

    // Build breadcrumbs from all pages except the last
    const breadcrumbPages = pages.slice(0, -1);
    const breadcrumbs = pagesToUrl(breadcrumbPages).substring(1); // Remove leading slash

    return {
      ...deepLink,
      id: pagesToUrl([currentPage]).substring(1), // Current page segment without leading slash
      loadChildren: null,
      data: currentPage.params,
      breadcrumbs: breadcrumbs,
    }
  }




  serialize(segment: NavSegment): string {
    if (!segment) {
      return '/';
    }

    // Build the full navigation stack URL in the new format
    let url = '/';

    // Add breadcrumbs (parent pages) first
    if (segment.breadcrumbs && segment.breadcrumbs.trim() !== '') {
      url += segment.breadcrumbs + '/';
    }

    // Add the current page segment
    url += segment.id;

    return url;
  }

  serializeComponent(component: Type<CsPageComponentInterface<any>>, data: any, breadcrumbs?: string): NavSegment {
    if (component) {
      const link = getAllDeepLinks().find(link => link.component === component);
      if (link) {
        const segment = this.createSegment(link, data);
        if (segment && breadcrumbs) {
          segment.breadcrumbs = breadcrumbs;
        }
        return segment;
      }
    }
    return null;
  }

  /**
   * Build the NavSegment for one page. Its `id` is the page's bare route segment — a page
   * IDENTITY, not an address, and it deliberately carries none of the page's parameters.
   *
   * The only thing that reads this `id` is `cs-nav.ts`, which stores `serialize(...)` of it
   * as `Page.id`; that value is then compared in `CsNavComponent.popTo`.
   *
   * df-3a1c41a56b8b: this method used to pick parameters out of `data` by scanning the route
   * segment for `:<name>` placeholders. No row in `app-page-list.ts` declares such a
   * placeholder, so that scan always came back empty and the id was always the bare segment
   * anyway. The scan is gone; the id is unchanged. It was the last of three copies of the
   * same dead rule — `#5124` removed one, `df-a776d36e5110` the other.
   */
  private createSegment(configLink: DeepLinkMetadata, data: any): NavSegment {
    // Get the page segment name (first part of segment)
    const segmentName = configLink.segment.split('/')[0];

    const page: PageWithParams = { segment: segmentName, params: {} };
    const pageUrl = pagesToUrl([page]).substring(1); // Remove leading slash

    return {
      ...configLink,
      id: pageUrl,
      data: data || {},
    };
  }
}